// apps/web/src/widgets/template-renderer.tsx
//
// Phase 6 — D-11 dispatcher. Looks up the renderer FC in `templateRenderers` by
// widget.templateId, re-validates widget.config against TEMPLATE_CATALOG[id]
// (defense-in-depth — Pass 2A should have caught issues earlier), and renders
// the matched FC with the standard prop bundle.
//
// Failure modes:
//   - rendered outside <RecipeStateProvider>: throws (programmer error)
//   - unknown templateId in dev: throws (surfaces missing registry entry)
//   - unknown templateId in prod: renders destructive Alert
//   - templateId known but config fails configSchema.safeParse: renders Alert
import {
  type RecipeDefinitionV1,
  TEMPLATE_CATALOG,
  type TemplateId,
  type Widget,
} from "@the-recipe-spread/shared";
import { AlertCircle } from "lucide-react";
import { useContext } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RecipeStateContext } from "./recipe-state-provider.js";
import { templateRenderers } from "./template-renderers.js";
import { UnregisteredTemplatePlaceholder } from "./UnregisteredTemplatePlaceholder.js";
import type { WidgetState } from "./widget-renderer-props.js";

// Catalog-membership oracle (D2). `id in TEMPLATE_CATALOG` distinguishes a
// cataloged-but-unregistered template (graceful placeholder — the four
// downstream-owned templates) from a genuinely-unknown id (programmer/data
// error — keep the hard dev-throw / prod Alert). Object.hasOwn is the repo's
// established own-property membership check (catalog test + visible-when use it);
// it guards against prototype-chain false positives without inheriting them.
function isCataloged(id: string): id is TemplateId {
  return Object.hasOwn(TEMPLATE_CATALOG, id);
}

export interface TemplateRendererProps {
  widget: Widget;
  recipe: RecipeDefinitionV1;
}

function WidgetRenderFailure({ message }: { message: string }): React.ReactElement {
  return (
    <Alert variant="destructive">
      <AlertCircle size={16} />
      <AlertTitle>Widget couldn't render</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

export function TemplateRenderer({ widget, recipe }: TemplateRendererProps): React.ReactElement {
  const ctx = useContext(RecipeStateContext);
  if (ctx === null) {
    throw new Error("<TemplateRenderer> must be rendered inside <RecipeStateProvider>");
  }

  const Renderer = templateRenderers.get(widget.templateId as TemplateId);

  if (Renderer === undefined) {
    // D2 relaxation: a templateId that EXISTS in TEMPLATE_CATALOG but has no
    // registered renderer (the four downstream-owned templates) renders a
    // NEUTRAL placeholder in BOTH dev and prod — graceful degradation, not an
    // error. This short-circuits BEFORE the config-safeParse step, so a
    // cataloged-but-unregistered widget never reaches re-validation.
    if (isCataloged(widget.templateId)) {
      return <UnregisteredTemplatePlaceholder templateId={widget.templateId} name={widget.name} />;
    }
    // Genuinely-unknown id (absent from the catalog) = programmer/data error:
    // keep the hard dev-throw / prod destructive Alert unchanged.
    if (import.meta.env["MODE"] !== "production") {
      throw new Error(
        `Unknown templateId at runtime: ${widget.templateId}. ` +
          "Add it to apps/web/src/widgets/template-renderers.ts.",
      );
    }
    return (
      <WidgetRenderFailure
        message={`This widget couldn't render — unknown template: ${widget.templateId}`}
      />
    );
  }

  // Defense-in-depth re-validation: Pass 2A should have caught any failure
  // before we got here, but a recipe persisted before Phase 6 typed schemas
  // shipped could still slip through. Fail-closed via the Alert.
  const template = TEMPLATE_CATALOG[widget.templateId as TemplateId];
  const parsed = template.configSchema.safeParse(widget.config);
  if (!parsed.success) {
    const firstIssueMessage = parsed.error.issues[0]?.message ?? "Invalid widget config";
    return (
      <WidgetRenderFailure
        message={`This widget couldn't render — invalid configuration: ${firstIssueMessage}`}
      />
    );
  }

  const state = ctx.states.get(widget.instanceId);
  const onStateChange = (next: WidgetState): void => {
    ctx.setWidgetState(widget.instanceId, next);
  };

  // The map value is React.FC<unknown> (type-erased — see template-renderers.ts
  // header). The narrowed type-map in config-for-template.ts is what enforces
  // correctness statically inside each renderer. The dispatcher passes the
  // standardized prop bundle.
  const props: Record<string, unknown> = {
    widget,
    config: parsed.data,
    recipe,
    state,
    onStateChange,
  };
  return <Renderer {...props} />;
}
